import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import Navbar from "./Navbar";
import { Link, useNavigate } from "react-router-dom";
import "./ViewBlogs.css";
import SponsoredAd from "./Ads/SponsoredAd";
import StatusAvatar from "./StatusAvatar";
import useStatusIndex from "../hooks/useStatusIndex";
import Comments from "./Comments";
import LikeButton from "./LikeButton";
import BuyTreat from "./Treat/BuyTreat";
import BannerAds from "./BannerAds";
import { getFeed, getRecentSearches, rememberSearch } from "../services/feedApi";
import { recordBoostView } from "../services/boostApi";
import { serveFeedAds } from "../services/adsApi";
import { adSlotAfter, adSlotsForPage } from "../utils/adPlacement";
import useSmartLinks from "../hooks/useSmartLinks";
import { getContentPreferences, FALLBACK_OPTIONS } from "../services/preferencesApi";
import { buildBlogFilter, ALL_LANGUAGES } from "../utils/contentSearch";
import ListLoader, { useProgressiveList } from "./ProgressiveList";

// How much of the ranked feed to pull per request, and how many requests to
// make. This page has always loaded the list up front and paginated it
// client-side; the feed is simply where the list now comes from.
const FEED_PAGE_SIZE = 100;
const MAX_FEED_PAGES = 5;

function ViewBlogs() {
  const smartLinks = useSmartLinks("readblogs");
  const [blogs, setBlogs]                   = useState([]);
  const [filteredBlogs, setFilteredBlogs]   = useState([]);
  const [modalContent, setModalContent]     = useState(null);
  const [expandedBlogs, setExpandedBlogs]   = useState({});
  const [token]                             = useState(localStorage.getItem("token"));
  // Starts true so the first paint is the loading spinner, not a flash of
  // the "No blogs" empty state before the feed request has even started.
  const [loading, setLoading]               = useState(true);
  const [loadError, setLoadError]           = useState("");
  const [searchLoading, setSearchLoading]   = useState(false);
  const [searchTerm, setSearchTerm]         = useState("");
  const [authorProfiles, setAuthorProfiles] = useState({});
  const [savedBlogs, setSavedBlogs]         = useState([]);
  const [followStatus, setFollowStatus]     = useState({});
  const [followLoading, setFollowLoading]   = useState({});
  const [openCommentId, setOpenCommentId]   = useState(null);
  const [shareToast, setShareToast]         = useState(null);
  const [viewCounts, setViewCounts]         = useState({});
  const [commentCounts, setCommentCounts]   = useState({});
  const [shareCounts, setShareCounts]       = useState({});
  const [saveCounts, setSaveCounts]         = useState({});
  const [myProfilePic, setMyProfilePic]     = useState(
    "https://cdn-icons-png.flaticon.com/512/149/149071.png"
  );
  // ── LANGUAGE / CATEGORY ── saved Preferred Languages (applied while
  // searching) and this search's explicit filters ("" = use the defaults).
  // The filters are page state only: they are never saved.
  const [prefLanguages, setPrefLanguages]   = useState([]);
  const [contentOptions, setContentOptions] = useState(FALLBACK_OPTIONS);
  const [filterLanguage, setFilterLanguage] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [searchScope, setSearchScope]       = useState(null);

  // Batch size for progressive loading (was the per-page size). A new search
  // or filter (a new filteredBlogs list) starts again from the first batch.
  const blogsPerPage         = 10;
  const blogList             = useProgressiveList(filteredBlogs, blogsPerPage, filteredBlogs);
  const canvasRef            = useRef(null);
  const videoRefs            = useRef({});
  const modalVideoRefHandler = useRef(null);
  const viewObserverRef      = useRef(null);
  const viewedRef            = useRef(new Set());
  // ── PAID BOOST ──
  // blogId → boostId for the blogs the server served with boost priority to
  // THIS reader, plus the ones already reported as read in this session. The
  // server is the real ledger (one record per reader, ever); these two only
  // stop us sending the same request twice while the page is open.
  const boostedRef           = useRef(new Map());
  const boostReportedRef     = useRef(new Set());
  // Holds the in-flight 300ms search timer so a new keystroke cancels it.
  const searchTimeoutRef     = useRef(null);
  // ── SPONSORED ADS ── one ad after every 7 blogs. slot number → served ad.
  // Each slot is requested once per page load, so paging back and forth or
  // re-rendering never swaps or re-requests an ad.
  const [feedAds, setFeedAds]   = useState({});
  const requestedAdSlotsRef     = useRef(new Set());
  const userId               = localStorage.getItem("userId");
  const username             = localStorage.getItem("username");

  const navigate = useNavigate();
  const { getStatusFor } = useStatusIndex();

  const BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://success-blog-app-backend.onrender.com";

  // ── AUTHOR PROFILE CLICK ──
  // Tapping the profile picture opens that author's profile page, the same
  // destination as their name next to it.
  const openAuthorProfile = (authorName) => {
    if (!authorName) return;
    navigate(`/authorprofile/${encodeURIComponent(authorName)}`);
  };

  // ── FETCH MY PROFILE PIC ──
  useEffect(() => {
    if (!username) return;
    axios
      .get(`${BASE_URL}/api/profile/username/${username}`)
      .then((res) => { if (res.data.profilePic) setMyProfilePic(res.data.profilePic); })
      .catch(() => {});
  }, [BASE_URL, username]);

  // ── CONTENT PREFERENCES ──
  // Read on every visit, so a change made in Settings applies to the very
  // next search. Without them the search behaves exactly as it always has.
  useEffect(() => {
    if (!token) return;
    getContentPreferences()
      .then((prefs) => {
        setPrefLanguages(prefs.preferredLanguages);
        setContentOptions(prefs.options);
      })
      .catch(() => {});
  }, [token]);

  // ── PARTICLE CANVAS  (LIGHT) ──
  // Fewer particles, no connecting lines and a 20fps cap, so the background
  // costs almost nothing on a phone.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    const particles = Array.from({ length: 25 }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.2,
      vy: (Math.random() - 0.5) * 0.2,
      r:  Math.random() * 1.2 + 0.5,
    }));
    let animId;
    let lastTime = 0;
    const draw = (time) => {
      // 20fps only — smooth but no lag
      if (time - lastTime < 50) {
        animId = requestAnimationFrame(draw);
        return;
      }
      lastTime = time;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        ctx.beginPath();
        ctx.arc(particles[i].x, particles[i].y, particles[i].r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(167,139,250,0.5)";
        ctx.fill();
        particles[i].x += particles[i].vx;
        particles[i].y += particles[i].vy;
        if (particles[i].x < 0 || particles[i].x > canvas.width)  particles[i].vx *= -1;
        if (particles[i].y < 0 || particles[i].y > canvas.height) particles[i].vy *= -1;
      }
      animId = requestAnimationFrame(draw);
    };
    draw(0);
    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);

  // ── SAVED BLOGS ──
  const fetchSavedBlogs = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/blogs/saved`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedBlogs(res.data.savedBlogs.map((b) => (typeof b === "object" ? b._id : b)));
    } catch (err) { console.error(err); }
  }, [BASE_URL, token]);

  useEffect(() => { fetchSavedBlogs(); }, [fetchSavedBlogs]);

  const handleSaveToggle = async (blogId) => {
    try {
      const res = await axios.post(`${BASE_URL}/api/blogs/${blogId}/save`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (typeof res.data?.saveCount === "number") {
        setSaveCounts((prev) => ({ ...prev, [blogId]: res.data.saveCount }));
      }
      fetchSavedBlogs();
    } catch (err) { console.error(err); }
  };

  // ── AUTHOR PROFILES ──
  const fetchAuthorProfile = async (authorName) => {
    if (!authorName) return;
    try {
      if (authorProfiles[authorName] !== undefined) return;
      setAuthorProfiles((prev) => ({ ...prev, [authorName]: "" }));
      const res = await axios.get(`${BASE_URL}/api/profile/username/${authorName}`);
      setAuthorProfiles((prev) => ({
        ...prev,
        [authorName]: res.data.profilePic || "https://cdn-icons-png.flaticon.com/512/149/149071.png",
      }));
    } catch {
      setAuthorProfiles((prev) => ({
        ...prev,
        [authorName]: "https://cdn-icons-png.flaticon.com/512/149/149071.png",
      }));
    }
  };

  // ── FOLLOW ──
  const checkFollowStatuses = async (authors) => {
    const statuses = {};
    await Promise.all(
      authors.map(async (author) => {
        try {
          const res = await axios.get(`${BASE_URL}/api/users/${author}/follow-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          statuses[author] = res.data.isFollowing;
        } catch { statuses[author] = false; }
      })
    );
    setFollowStatus(statuses);
  };

  const toggleFollow = async (authorName) => {
    if (!authorName || followLoading[authorName]) return;
    const current = followStatus[authorName];
    setFollowStatus((prev) => ({ ...prev, [authorName]: !current }));
    setFollowLoading((prev) => ({ ...prev, [authorName]: true }));
    try {
      await axios.post(`${BASE_URL}/api/users/${authorName}/follow`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      setFollowStatus((prev) => ({ ...prev, [authorName]: current }));
    } finally {
      setFollowLoading((prev) => ({ ...prev, [authorName]: false }));
    }
  };

  // ── FETCH BLOGS (MIXED FEED) ──
  // ONE feed: latest blogs from everybody, blogs by people you follow and
  // blogs matching your interests, merged and ranked server-side. Following
  // and interest are ranking BOOSTS, never filters, so the latest blogs from
  // the whole platform always come through — including for a brand-new
  // account that follows nobody.
  //
  // The response items are the same blog documents GET /api/blogs returns, so
  // everything below this function — the cards, the counters, the ads, the
  // numbered pagination — is unchanged.
  // The list is pulled ONCE, on mount. Typing in the search box no longer
  // re-requests the feed — it filters this list on the device, which is what
  // makes the search feel instant.
  const fetchAllBlogs = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");

      const interests = getRecentSearches();

      // Page 1 is requested WITHOUT a seed, so the server mints a fresh one
      // and the order differs on every visit/refresh. The remaining pages
      // reuse that seed, which is what keeps the list stable and free of
      // duplicates while we pull it.
      const collected = [];
      let seed;
      for (let page = 1; page <= MAX_FEED_PAGES; page++) {
        const data = await getFeed({
          query: "",
          interests,
          seed,
          page,
          limit: FEED_PAGE_SIZE,
        });
        seed = data.seed;
        collected.push(...(data.items || []));
        if (!data.hasMore) break;
      }

      const safeBlogs = collected.map((blog) => ({
        ...blog,
        likes:        blog.likes    || [],
        dislikes:     blog.dislikes || [],
        likeCount:    blog.likes?.length    || 0,
        dislikeCount: blog.dislikes?.length || 0,
      }));
      setBlogs(safeBlogs);
      setFilteredBlogs(safeBlogs);

      // Which of these blogs arrived with paid boost priority. Rebuilt on
      // every load, so once the server stops boosting a blog for this reader
      // it stops being reported too.
      const boosted = new Map();
      safeBlogs.forEach((b) => {
        if (b.boosted && b.boostId) boosted.set(b._id, b.boostId);
      });
      boostedRef.current = boosted;

      // Seed engagement counters from the API response. They live in their
      // own maps (like viewCounts) so a share/save can update one card
      // without touching `blogs` — which would re-run the search filter and
      // bounce the reader back to page 1.
      const comments = {}, shares = {}, saves = {};
      safeBlogs.forEach((b) => {
        comments[b._id] = b.commentCount ?? 0;
        shares[b._id]   = b.shareCount   ?? 0;
        saves[b._id]    = b.saveCount    ?? 0;
      });
      setCommentCounts(comments);
      setShareCounts(shares);
      setSaveCounts(saves);

      const uniqueAuthors = [...new Set(safeBlogs.map((b) => b.author).filter(Boolean))];
      uniqueAuthors.forEach(fetchAuthorProfile);
      checkFollowStatuses(uniqueAuthors);
    } catch (err) {
      console.error(err);
      setLoadError("Could not load blogs. Please check your connection and try again.");
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAllBlogs(); }, [fetchAllBlogs]);

  // ── SEARCH ──
  // Local, 300ms after the last keystroke: the already-loaded list is filtered
  // on the device, so results appear immediately instead of waiting on a
  // request. The term is still remembered so the feed's ranking learns from it.
  //
  // The title/content/author match is the same as always; buildBlogFilter
  // adds the language rules (saved languages, or the one language the query
  // or the dropdown asks for) and the category filter on top of it.
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    const filter = buildBlogFilter({
      query: searchTerm,
      filterLanguage,
      filterCategory,
      preferredLanguages: prefLanguages,
      options: contentOptions,
    });

    // No search and no filter -> show all
    if (!filter) {
      setFilteredBlogs(blogs);
      setSearchScope(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);

    searchTimeoutRef.current = setTimeout(() => {
      const s = searchTerm.trim().toLowerCase();

      // Local fast filter - instant
      setFilteredBlogs(blogs.filter(filter.matches));
      setSearchScope(filter.scope);
      setSearchLoading(false);

      // Remember search for ranking
      if (s.length > 2) rememberSearch(s);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, blogs, filterLanguage, filterCategory, prefLanguages, contentOptions]);

  const clearSearch = () => {
    setSearchTerm("");
    setFilterLanguage("");
    setFilterCategory("");
  };

  // ── UNIQUE VIEW TRACKING ──
  const recordView = useCallback(async (blogId) => {
    if (!token) return;
    try {
      const res = await axios.post(
        `${BASE_URL}/api/blogs/${blogId}/view`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newCount = res.data?.viewCount;
      if (typeof newCount === "number") {
        setViewCounts((prev) =>
          prev[blogId] === newCount ? prev : { ...prev, [blogId]: newCount }
        );
      }
    } catch {
      viewedRef.current.delete(blogId);
    }
  }, [BASE_URL, token]);

  // ── BOOST: THIS READER HAS NOW READ THE BOOSTED BLOG ──
  // Reported at the same moment the blog counts as viewed. The server records
  // the reader ONCE for that blog and never again, so a refresh, reopening
  // the page or restarting the app cannot move the campaign's counter — and
  // from the next load the blog is ranked normally for this reader.
  const reportBoostRead = useCallback(async (blogId) => {
    if (!token) return;
    if (!boostedRef.current.has(blogId)) return;
    if (boostReportedRef.current.has(blogId)) return;

    boostReportedRef.current.add(blogId);
    try {
      await recordBoostView(blogId);
    } catch (err) {
      // Let a genuinely failed report be retried on the next impression.
      boostReportedRef.current.delete(blogId);
    }
  }, [token]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = entry.target.dataset.blogId;
          if (id && !viewedRef.current.has(id)) {
            viewedRef.current.add(id);
            recordView(id);
            reportBoostRead(id);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );
    viewObserverRef.current = observer;
    return () => observer.disconnect();
  }, [recordView, reportBoostRead]);

  const observeCard = useCallback((node) => {
    if (node && viewObserverRef.current) viewObserverRef.current.observe(node);
  }, []);

  // ── SPONSORED ADS FOR THE LOADED BLOGS ──
  // Asks only for the slots among the blogs loaded so far that have not been
  // asked for yet, sending the 7 blogs before each slot as its context and the
  // ads already on screen so another campaign is preferred.
  const visibleCount = blogList.visibleItems.length;
  useEffect(() => {
    if (!token || loading) return;
    const wanted = adSlotsForPage(filteredBlogs, 1, visibleCount)
      .filter((s) => !requestedAdSlotsRef.current.has(s.slot));
    if (!wanted.length) return;
    wanted.forEach((s) => requestedAdSlotsRef.current.add(s.slot));

    const onScreen = Object.values(feedAds).map((entry) => entry.ad.id);
    serveFeedAds(wanted, onScreen)
      .then((served) => {
        if (!served.length) return;
        setFeedAds((prev) => {
          const next = { ...prev };
          served.forEach((entry) => { next[entry.slot] = entry; });
          return next;
        });
      })
      .catch(() => {
        // Ads must never break the feed; the slot may be asked for again.
        wanted.forEach((s) => requestedAdSlotsRef.current.delete(s.slot));
      });
  }, [token, loading, filteredBlogs, visibleCount]);

  const toggleReadMore = (id) => setExpandedBlogs((prev) => ({ ...prev, [id]: !prev[id] }));
  const isSaved        = (id) => savedBlogs.includes(id);

  // ── TOGGLE COMMENTS ──
  const toggleComments = (blogId) => {
    setOpenCommentId((prev) => (prev === blogId ? null : blogId));
  };

  // ── SHARE ──
  const recordShare = useCallback(async (blogId) => {
    setShareCounts((prev) => ({ ...prev, [blogId]: (prev[blogId] ?? 0) + 1 }));
    try {
      const res = await axios.post(`${BASE_URL}/api/blogs/${blogId}/share`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (typeof res.data?.shareCount === "number") {
        setShareCounts((prev) => ({ ...prev, [blogId]: res.data.shareCount }));
      }
    } catch (err) {
      console.error("Share count failed:", err);
      setShareCounts((prev) => ({ ...prev, [blogId]: Math.max((prev[blogId] ?? 1) - 1, 0) }));
    }
  }, [BASE_URL, token]);

  const handleShare = async (blog) => {
    const url = `${window.location.origin}/blog/${blog._id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: blog.title, text: blog.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShareToast(blog._id);
        setTimeout(() => setShareToast(null), 2000);
      }
    } catch (err) {
      // Share sheet dismissed or clipboard blocked — nothing left the app,
      // so this must not count as a share.
      console.error(err);
      return;
    }
    recordShare(blog._id);
  };

  // ── VIDEO HELPERS ──
  const handleVideoPlay = (id) => {
    Object.keys(videoRefs.current).forEach((key) => {
      if (key !== id) { const v = videoRefs.current[key]; if (v && !v.paused) v.pause(); }
    });
  };
  const stopAllVideos = () => {
    Object.values(videoRefs.current).forEach((v) => {
      if (v && !v.paused) { v.pause(); v.currentTime = 0; }
    });
    modalVideoRefHandler.current?.pause();
  };
  const openModal  = (type, url) => { stopAllVideos(); setModalContent({ type, url }); };
  const closeModal = ()          => { stopAllVideos(); setModalContent(null); };

  const DEFAULT_PIC   = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
  const getProfilePic = (name) => {
    if (name === username) return myProfilePic || DEFAULT_PIC;
    return authorProfiles[name] || DEFAULT_PIC;
  };

  const currentBlogs = blogList.visibleItems;

  if (loading) return (
    <div className="vb-wrapper">
      <canvas ref={canvasRef} className="vb-canvas" />
      <Navbar />
      <div className="vb-loading">
        <div className="vb-spinner" />
        <p>Loading blogs...</p>
      </div>
    </div>
  );

  return (
    <div className="vb-wrapper">
      <canvas ref={canvasRef} className="vb-canvas" />
      <Navbar />

      <div className="viewblogs-container">

        <div className="vb-header">
          <div className="vb-badge">✦ Explore</div>
          <h1 className="vb-title">All Blogs</h1>
          <p className="vb-sub">Discover knowledge shared by our community</p>
        </div>

        <div className="vb-search-wrap" style={{ position: 'relative' }}>
          <span className="vb-search-icon">🔍</span>
          <input
            className="blog-search"
            placeholder="Search by title, content, or author..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', paddingRight: '40px' }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)',
                background: '#eee', border: 'none', borderRadius: '50%', width: '24px', height: '24px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              ✕
            </button>
          )}
          {searchLoading && (
            <div style={{ position: 'absolute', right: '45px', top: '50%', transform: 'translateY(-50%)' }}>
              <div style={{ width: '16px', height: '16px', border: '2px solid #eee', borderTop: '2px solid #8b5cf6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
            </div>
          )}
        </div>

        {/* LANGUAGE / CATEGORY — explicit, for this search only */}
        <div className="vb-filters">
          <select
            className="vb-filter-select"
            aria-label="Language"
            value={filterLanguage}
            onChange={(e) => setFilterLanguage(e.target.value)}
          >
            <option value="">{prefLanguages.length ? "My languages" : "Any language"}</option>
            {prefLanguages.length > 0 && <option value={ALL_LANGUAGES}>All languages</option>}
            {contentOptions.languages.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <select
            className="vb-filter-select"
            aria-label="Category"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All categories</option>
            {contentOptions.categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {searchScope && !searchLoading && (
          <p className="vb-filter-note">
            {searchScope.source === "preferences"
              ? `In your languages: ${searchScope.languages.join(", ")}`
              : `Showing ${searchScope.languages.join(", ")} blogs`}
          </p>
        )}

        {searchTerm && !searchLoading && (
          <p style={{ margin: '10px 0', color: '#666', fontSize: '14px' }}>
            {filteredBlogs.length} results for <b>"{searchTerm}"</b>
          </p>
        )}

        <div className="blogs-list">
          {currentBlogs.map((blog, index) => {
            const isExpanded    = expandedBlogs[blog._id];
            const authorPic     = getProfilePic(blog.author);
            const isLiked       = blog.likes?.some((l) =>
              typeof l === "object" ? l._id === userId : l === userId
            );
            const isCommentOpen = openCommentId === blog._id;
            const authorStatus  = getStatusFor(blog.author);

            // 1-based position in the WHOLE list (blogs load progressively,
            // so the rendered index already is the whole-list index).
            const position = index + 1;
            // Sponsored slot after every 7th blog of the WHOLE list.
            const adSlot  = adSlotAfter(position);
            const adEntry = adSlot ? feedAds[adSlot] : null;

            return (
              <div key={blog._id}>
                <div
                  className={`blog-card ${blog.boosted ? "blog-card-boosted" : ""}`}
                  ref={observeCard}
                  data-blog-id={blog._id}
                  {...smartLinks.cardProps(position)}
                >

                  {/* BOOSTED — a paid campaign put this blog first for you */}
                  {blog.boosted && <div className="blog-boosted-badge">🚀 Boosted</div>}

                  {/* AUTHOR ROW */}
                  <div className="author-section">
                    <StatusAvatar
                      src={authorPic}
                      alt={blog.author}
                      size={46}
                      hasStatus={Boolean(authorStatus)}
                      hasUnviewed={Boolean(authorStatus?.hasUnviewed)}
                      onClick={() => openAuthorProfile(blog.author)}
                      title={`View ${blog.author}'s profile`}
                    />
                    <p>
                      <Link to={`/authorprofile/${blog.author}`}>{blog.author}</Link>
                    </p>
                    <button
                      className={`follow-btn ${followStatus[blog.author] ? "following" : ""}`}
                      onClick={() => toggleFollow(blog.author)}
                      disabled={followLoading[blog.author]}
                    >
                      {followLoading[blog.author] ? "..." : followStatus[blog.author] ? "Following" : "Follow"}
                    </button>
                  </div>

                  {/* TITLE + CONTENT */}
                  <h2>{blog.title}</h2>
                  <p>
                    {isExpanded ? blog.content : blog.content.slice(0, 150)}
                    {blog.content.length > 150 && (
                      <span className="read-more" onClick={() => toggleReadMore(blog._id)}>
                        {isExpanded ? " Read Less" : " Read More"}
                      </span>
                    )}
                  </p>

                  {/* MEDIA — auto-fits any orientation/resolution across all screen sizes */}
                  {blog.image && (
  <div
    className="media-frame"
    style={{ aspectRatio: (blog.aspectRatio || "4:5").replace(":", "/") }}
    onClick={() => openModal("image", blog.image)}
  >
    <img
      src={blog.image}
      className="blog-image"
      alt=""
      loading="lazy"
    />
  </div>
)}
                  {blog.video && (
                    <div className="media-frame media-frame-video">
                      <video
                        src={blog.video}
                        controls
                        playsInline
                        preload="metadata"
                        ref={(el) => (videoRefs.current[blog._id] = el)}
                        onPlay={() => handleVideoPlay(blog._id)}
                        className="blog-video"
                      />
                    </div>
                  )}

                {/* ACTIONS */}
                  <div className="blog-actions">
                    <div className="actions-left">

                      <LikeButton
                        blogId={blog._id}
                        initialLikes={blog.likeCount || 0}
                        initialLiked={isLiked}
                        token={token}
                      />

                      {!blog.hideComments && (
                        <button
                          className={`action-btn comment-btn ${isCommentOpen ? "active" : ""}`}
                          onClick={() => toggleComments(blog._id)}
                          type="button"
                        >
                          <svg viewBox="0 0 24 24" className="action-icon comment-icon">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                          <span>{commentCounts[blog._id] ?? 0}</span>
                        </button>
                      )}

                      {blog.allowShare !== false && (
                        <div className="share-wrapper">
                          <button
                            className="action-btn share-btn"
                            onClick={() => handleShare(blog)}
                            type="button"
                          >
                            <svg viewBox="0 0 24 24" className="action-icon share-icon">
                              <line x1="22" y1="2" x2="11" y2="13" />
                              <polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                            <span>{shareCounts[blog._id] ?? 0}</span>
                          </button>
                          {shareToast === blog._id && (
                            <span className="share-toast">Link copied!</span>
                          )}
                        </div>
                      )}

                      {/* BUY ME A TREAT — only on posts the author enabled, never on your own */}
                      {blog.treatsEnabled && token && blog.author !== username && (
                        <BuyTreat blogId={blog._id} />
                      )}

                    </div>

                    <div className="actions-right">
                      {blog.allowSave !== false && (
                        <button
                          className={`action-btn save-btn ${isSaved(blog._id) ? "saved" : ""}`}
                          onClick={() => handleSaveToggle(blog._id)}
                          type="button"
                        >
                          <svg viewBox="0 0 24 24" className="action-icon save-icon">
                            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                          </svg>
                          <span>{saveCounts[blog._id] ?? 0}</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* COMMENTS */}
                  {!blog.hideComments && (
                    <Comments
                      blogId={blog._id}
                      blogAuthor={blog.author}
                      currentUsername={username}
                      currentUserPic={myProfilePic}
                      isOpen={isCommentOpen}
                    />
                  )}
                  <div className="blog-meta">
                    <span className="blog-upload-time">
                      🕒 {new Date(blog.createdAt).toLocaleString()}
                    </span>
                    {(blog.language || blog.category) && (
                      <span className="blog-lang-cat">
                        {[blog.language, blog.category].filter(Boolean).join(" · ")}
                      </span>
                    )}
                    <span className="blog-views" title="Unique views">
                      <svg viewBox="0 0 24 24" className="view-icon">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      {viewCounts[blog._id] ?? blog.viewCount ?? 0}
                    </span>
                  </div>
                </div>

                {/* ── BANNER AD after every 3rd blog ── */}
                {position % 3 === 0 && (
                  <BannerAds key={`banner-${blog._id}`} />
                )}

                {/* ── SPONSORED AD after every 7 blogs ── */}
                {adEntry && (
                  <SponsoredAd key={`ad-${adSlot}`} ad={adEntry.ad} token={adEntry.token} viewToken={adEntry.viewToken} />
                )}
              </div>
            );
          })}
        </div>

        {loadError && (
          <div className="plist-state" role="alert">
            <p>{loadError}</p>
            <button type="button" className="plist-retry-btn" onClick={fetchAllBlogs}>
              Try again
            </button>
          </div>
        )}

        {filteredBlogs.length === 0 && !loading && !searchLoading && !loadError && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <h3>{searchTerm ? `No blogs found for "${searchTerm}"` : "No blogs match these filters"}</h3>
            <button
              onClick={clearSearch}
              style={{ marginTop: '10px', padding: '10px 20px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
            >
              Show All
            </button>
          </div>
        )}

        {/* Progressive loading — replaces the old per-page number buttons */}
        <ListLoader list={blogList} total={filteredBlogs.length} />


      </div>

      {modalContent && (
        <div className="image-modal" onClick={closeModal}>
          <span>×</span>
          {modalContent.type === "image" ? (
            <img src={modalContent.url} alt="" />
          ) : (
            <video src={modalContent.url} controls playsInline autoPlay />
          )}
        </div>
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default ViewBlogs;
